import { useState, useEffect, useCallback } from "react"
import dayjs, { Dayjs } from "dayjs"
import {
  CompressOutlined,
  DownOutlined,
  ExpandOutlined,
  FilePdfOutlined,
  InfoCircleOutlined,
  RightOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
} from "@ant-design/icons"
import { cloneDeep } from "lodash"
import { Badge, Button, Card, Empty, message, Modal, Progress, Skeleton, Tooltip, Dropdown, Row, Col, Space, Tag, Typography } from "antd"
import styled from "styled-components"
import { useStoreState, StateMapper } from "easy-peasy"
import { AppDataStore } from "../appData/types"
// Import directly (not the "./utils" barrel) to avoid a circular-import
// ordering issue that leaves these undefined under CJS/Jest.
import { swimminglyApi, gen } from "./utils/helperFunctions"
import { IClubSeason, IMeet, IClub, CustomEventEmitter } from "./utils/sharedDataTypes"
import { PersistentClubBlockSlot } from "./PersistentClubBlock"
import ContentPanel from "./ContentPanel"
import swimminglyImage from "../images/Swimmingly_Illustrations_03.png"
import CreateMeetModal from "./CreateMeetModal"
import MeetActionsModal from "./MeetActionsModal"
import { useFeatureAccess } from "./FeatureAccessContext"
import { FeatureKey } from "../constants/featureAccess"
import RemovedMeetModal from "./RemovedMeetModal"
import SeasonMeetCard, { ISeasonMeetWithClubs, getMeetPublishLabel } from "./SeasonMeetCard"
import MeetClubStatus from "./MeetClubStatus"
import { faCalendar, faGear, faHouse, faRankingStar } from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import VirtualMeetInvitationResponseModal from "./VirtualMeetInvitationResponseModal"
import VirtualMeetShellEditorModal from "./VirtualMeetShellEditorModal"
import SharedVirtualMeetInvitationCard from "./SharedVirtualMeetInvitationCard"
import ChampionshipStandingsModal from "./ChampionshipStandingsModal"
import SharedAcceptedVirtualMeetCard from "./SharedAcceptedVirtualMeetCard"
import { useHistory, useLocation } from "react-router-dom"

export { default as MeetClubStatus } from "./MeetClubStatus"

interface SwimMeetResource {
  id: number
  title: string
  description: string | null
  category: string | null
  type: "upload" | "link"
  url: string | null
  cdnUrl: string | null
  openInNewTab: boolean
}

export const updateSeasonMeetArrayListener = new CustomEventEmitter()

const TEAM_SECTION_AUTO_EXPAND_THRESHOLD = 4

export const StyledSeasonDisplay = styled.div<{ $isFirstSeason?: boolean }>`
  margin-top: ${({ $isFirstSeason }) => ($isFirstSeason ? "8px" : "56px")};

  .swim-meets {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 20px 25px;
    padding: 0 16px;
    box-sizing: border-box;
    width: 100%;
  }

  .empty-image {
    height: 130px;
    width: 162px;
    margin: auto;
  }

  .swim-meet {
    box-shadow: 0 1px 2px 2px rgba(0, 0, 0, 0.04);
    border: 1px solid rgba(0, 0, 0, 0.06);
    border-radius: 5px;
    padding: 10px;
    cursor: pointer;
    min-width: 0; /* Prevent overflow in grid */
  }

  .shared-virtual-shell {
    margin-top: 20px;
  }

  .shared-virtual-summary-card {
    border-radius: 22px;
    border: 1px solid rgba(22, 119, 255, 0.12);
    background: linear-gradient(180deg, rgba(247, 251, 255, 0.98) 0%, rgba(255, 255, 255, 1) 100%);
    box-shadow: 0 18px 42px rgba(16, 18, 66, 0.08);
  }

  .shared-virtual-summary-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    flex-wrap: wrap;
    margin-bottom: 10px;
  }

  .season-section-tabs {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin: 6px 0 18px 40px;
    padding: 4px;
    border-radius: 999px;
    background: rgba(16, 18, 66, 0.05);
  }

  .season-section-tab {
    border: none;
    background: transparent;
    color: rgba(16, 18, 66, 0.68);
    border-radius: 999px;
    padding: 7px 12px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    transition: background 160ms ease, color 160ms ease, box-shadow 160ms ease;
  }

  .season-section-tab.active {
    background: #ffffff;
    color: #101242;
    box-shadow: 0 2px 8px rgba(16, 18, 66, 0.12);
  }

  .season-section-tab:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  .season-section-empty {
    padding: 0 16px;
  }
  }

  .shared-virtual-summary-title {
    font-size: 24px;
    line-height: 1.2;
    font-weight: 700;
    color: #101242;
    margin: 0;
  }

  .shared-virtual-summary-copy {
    max-width: 760px;
    font-size: 14px;
    line-height: 1.6;
    color: rgba(16, 18, 66, 0.72);
    margin-bottom: 18px;
  }

  .shared-virtual-count {
    background: rgba(22, 119, 255, 0.1);
    border-radius: 999px;
    padding: 8px 12px;
    color: #1677ff;
    font-weight: 700;
    font-size: 12px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .shared-virtual-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    gap: 16px;
    padding: 0 16px;
    box-sizing: border-box;
    width: 100%;
    transition: gap 180ms ease, grid-template-columns 180ms ease;
  }

  .season-accepted-virtual-grid {
    grid-template-columns: 1fr;
  }

  .season-accepted-virtual-grid-compact {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 14px;
  }

  .shared-virtual-meet-card {
    min-width: 0;
    border-radius: 5px;
    border: 1px solid rgba(0, 0, 0, 0.06);
    background: linear-gradient(180deg, rgba(240, 247, 255, 0.92) 0%, rgba(255, 255, 255, 0.99) 100%);
    box-shadow: 0 1px 2px 2px rgba(0, 0, 0, 0.04);
    transition: transform 180ms ease, box-shadow 180ms ease, opacity 180ms ease, padding 180ms ease, border-radius 180ms ease;
  }

  .season-accepted-virtual-card {
    border-left-width: 4px;
    border-left-style: solid;
    border-left-color: #1677ff;
    background: linear-gradient(180deg, rgba(236, 246, 255, 0.72) 0%, rgba(255, 255, 255, 0.99) 100%);
  }

  .shared-virtual-meet-card .ant-card-body {
    padding: 16px 18px;
  }

  .shared-virtual-meet-card-compact .ant-card-body {
    padding: 12px 14px;
  }

  .season-accepted-virtual-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 24px rgba(16, 18, 66, 0.10);
  }

  .virtual-card-header {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .virtual-card-title {
    margin: 0;
    color: #101242;
  }

  .virtual-card-meta {
    margin-top: 8px;
    margin-bottom: 0;
    font-size: 13px;
    line-height: 1.55;
    font-weight: 400;
    color: rgba(16, 18, 66, 0.72);
  }

  .virtual-card-status-row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
    margin-bottom: 8px;
  }

  .virtual-card-section-header {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
    margin-bottom: 8px;
  }

  .virtual-card-manage-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 999px;
    border: 1px solid rgba(16, 18, 66, 0.12);
    background: rgba(255, 255, 255, 0.9);
    color: #101242;
    opacity: 0;
    pointer-events: none;
    transition: opacity 160ms ease, transform 160ms ease, border-color 160ms ease, box-shadow 160ms ease;
  }

  .season-accepted-virtual-card:hover .virtual-card-manage-button,
  .shared-virtual-meet-card-compact .virtual-card-manage-button {
    opacity: 1;
    pointer-events: auto;
  }

  .season-accepted-virtual-card:hover .virtual-card-manage-button {
    transform: translateY(-1px);
    box-shadow: 0 6px 14px rgba(16, 18, 66, 0.12);
    border-color: rgba(22, 119, 255, 0.35);
  }

  .season-section-toggle {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 600;
    color: #1677ff;
    cursor: pointer;
    user-select: none;
  }

  .season-meet-card {
    transition: transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease, padding 180ms ease, border-radius 180ms ease;
    border-left-width: 4px;
    border-left-style: solid;
    padding: 16px 18px;
  }

  .season-meet-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 24px rgba(16, 18, 66, 0.10);
  }

  .season-removed-card {
    border: 1px dashed rgba(89, 89, 89, 0.45);
    background: linear-gradient(180deg, rgba(250, 250, 250, 0.98) 0%, rgba(245, 245, 245, 0.98) 100%);
    box-shadow: none;
    opacity: 0.92;
  }

  .season-removed-card:hover {
    transform: none;
    box-shadow: 0 8px 18px rgba(0, 0, 0, 0.06);
  }

  .season-removed-meta {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px dashed rgba(89, 89, 89, 0.28);
    font-size: 12px;
    line-height: 1.6;
    color: rgba(0, 0, 0, 0.6);
  }

  .season-meet-rows {
    grid-template-columns: 1fr;
    gap: 16px;
    transition: gap 180ms ease, grid-template-columns 180ms ease;
  }

  .season-meet-rows-compact {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 14px;
  }

  .season-meet-card-compact {
    padding: 12px 14px;
  }

  .season-meet-card-compact .season-meet-row-header {
    margin-bottom: 8px;
  }

  .season-meet-card-compact .season-meet-meta {
    margin-top: 6px;
    font-size: 12px;
  }

  .season-meet-card-compact .season-status-summary {
    min-width: 0;
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
  }

  .season-compact-summary {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid rgba(16, 18, 66, 0.08);
    font-size: 12px;
    line-height: 1.5;
    color: rgba(16, 18, 66, 0.72);
    display: flex;
    justify-content: space-between;
    gap: 10px;
    flex-wrap: wrap;
  }

  .season-compact-summary strong {
    color: #101242;
  }

  .season-compact-card-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid rgba(16, 18, 66, 0.08);
    font-size: 12px;
    color: rgba(16, 18, 66, 0.66);
  }

  .season-compact-actions {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .season-status-editing-entries {
    border-left-color: #faad14;
    background: linear-gradient(180deg, rgba(255, 251, 230, 0.96) 0%, rgba(255, 255, 255, 1) 100%);
  }

  .season-status-heat-sheet-merged {
    border-left-color: #1677ff;
    background: linear-gradient(180deg, rgba(240, 247, 255, 0.98) 0%, rgba(255, 255, 255, 1) 100%);
  }

  .season-status-results-published {
    border-left-color: #52c41a;
    background: linear-gradient(180deg, rgba(246, 255, 237, 0.98) 0%, rgba(255, 255, 255, 1) 100%);
  }

  .season-meet-row-header {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
    margin-bottom: 12px;
  }

  .season-meet-row-title {
    min-width: 0;
    flex: 1 1 420px;
  }

  .season-meet-title-bar {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
  }

  .season-meet-status-stack {
    flex: 0 0 220px;
    min-width: 180px;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    text-align: right;
    gap: 4px;
  }

  .season-meet-row-title h3 {
    margin: 0;
    color: #101242;
    min-width: 0;
  }

  .season-meet-title-bar .ant-tag,
  .season-meet-status-stack .ant-tag {
    margin-inline-end: 0;
    flex: 0 0 auto;
  }

  .season-meet-meta {
    margin-top: 8px;
    font-size: 13px;
    line-height: 1.55;
    color: rgba(16, 18, 66, 0.72);
  }

  .season-status-summary {
    min-width: 180px;
    margin-left: auto;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 8px;
    text-align: right;
  }

  .season-status-detail {
    font-size: 12px;
    line-height: 1.5;
    color: rgba(16, 18, 66, 0.62);
  }

  .season-status-publish-label {
    font-size: 11px;
    line-height: 1.4;
    color: rgba(16, 18, 66, 0.62);
  }

  .season-virtual-leg-note {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 10px;
  }

  .season-virtual-leg-note-copy {
    font-size: 12px;
    color: rgba(16, 18, 66, 0.58);
  }

  .virtual-results-panel {
    flex: 0 0 260px;
    min-width: 220px;
    margin-left: auto;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    text-align: right;
  }

  .virtual-results-label-row {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.5;
    text-transform: uppercase;
    color: #595959;
    margin-bottom: 4px;
  }

  .virtual-results-button-row {
    margin-top: 8px;
  }

  .virtual-season-leg-card {
    border-color: rgba(22, 119, 255, 0.16);
    background: linear-gradient(180deg, rgba(248, 251, 255, 0.98) 0%, rgba(255, 255, 255, 1) 100%);
  }

  .season-meet-label-row {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 10px;
  }

  .season-meet-label-copy {
    font-size: 12px;
    color: rgba(16, 18, 66, 0.58);
  }

  .national-champs {
    box-shadow: 0 1px 2px 2px rgba(0, 0, 0, 0.04);
    border: 1px solid rgba(0, 0, 0, 0.06);
    border-radius: 5px;
    padding: 10px;
    cursor: pointer;
    background-color: var(--primaryblue);
    color: white;
  }

  .national-champs h3 {
    text-align: center;
    color: white;
  }

  h2.season-title {
    color: var(--darkgrey);
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
  }

  h2.season-title button {
    align-self: center;
  }

  .season-title-text {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }

  .season-title-controls {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    flex-wrap: wrap;
  }

  .season-header-card {
    margin-bottom: 18px;
    border-radius: 22px;
    border: 1px solid rgba(16, 18, 66, 0.08);
    box-shadow: 0 14px 32px rgba(16, 18, 66, 0.08);
    overflow: hidden;
  }

  .season-header-card .ant-card-body {
    padding: 18px 20px;
  }

  .season-header-card.season-status-active {
    background: linear-gradient(180deg, rgba(246, 250, 255, 0.98) 0%, rgba(255, 255, 255, 1) 100%);
  }

  .season-header-card.season-status-upcoming {
    background: linear-gradient(180deg, rgba(255, 251, 230, 0.92) 0%, rgba(255, 255, 255, 1) 100%);
  }

  .season-header-card.season-status-ended {
    background: linear-gradient(180deg, rgba(250, 250, 250, 0.98) 0%, rgba(255, 255, 255, 1) 100%);
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.04);
  }

  .season-header-card.season-collapsed {
    box-shadow: 0 8px 18px rgba(16, 18, 66, 0.05);
  }

  .season-header-shell {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    flex-wrap: wrap;
  }

  .season-header-main {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    min-width: 0;
    flex: 1 1 420px;
  }

  .season-header-toggle.ant-btn {
    width: 38px;
    min-width: 38px;
    height: 38px;
    border-radius: 999px;
    border: 1px solid rgba(16, 18, 66, 0.08);
    background: rgba(255, 255, 255, 0.88);
    color: #101242;
    box-shadow: 0 4px 12px rgba(16, 18, 66, 0.08);
  }

  .season-header-copy {
    min-width: 0;
    flex: 1 1 auto;
  }

  .season-header-title-row {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    margin-bottom: 6px;
  }

  .season-header-title.ant-typography {
    margin: 0;
    color: #101242;
  }

  .season-header-controls {
    align-items: center;
    justify-content: flex-end;
  }

  .season-header-add-button.ant-btn {
    border-radius: 999px;
    box-shadow: 0 8px 18px rgba(22, 119, 255, 0.18);
  }

  @media (max-width: 1024px) {
    .swim-meets {
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    }

    .season-meet-rows-compact,
    .season-accepted-virtual-grid-compact {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }

  @media (max-width: 700px) {
    .swim-meets,
    .shared-virtual-grid {
      grid-template-columns: 1fr;
      padding: 0 8px;
    }

    .shared-virtual-summary-card .ant-card-body {
      padding: 16px;
    }

    .shared-virtual-summary-title {
      font-size: 20px;
    }

    .season-meet-card {
      padding: 14px;
    }

    .season-status-summary {
      min-width: 0;
    }

    .season-meet-status-stack,
    .virtual-results-panel {
      flex-basis: 100%;
      min-width: 0;
    }

    .season-title-controls {
      width: 100%;
      justify-content: flex-end;
    }

    .season-header-card .ant-card-body {
      padding: 16px;
    }

    .season-header-shell {
      gap: 14px;
    }

    .season-header-main {
      gap: 12px;
      flex-basis: 100%;
    }

    .season-header-controls {
      width: 100%;
      justify-content: flex-start;
    }

    .season-section-tabs {
      margin: 6px 0 16px 8px;
      width: calc(100% - 8px);
      justify-content: space-between;
      gap: 4px;
    }

    .season-section-tab {
      flex: 1 1 0;
      text-align: center;
    }

    .season-compact-summary {
      flex-direction: column;
    }

    .season-meet-rows-compact,
    .season-accepted-virtual-grid-compact {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .virtual-card-manage-button {
      opacity: 1;
      pointer-events: auto;
    }
  }

  @media (max-width: 520px) {
    .season-meet-rows-compact,
    .season-accepted-virtual-grid-compact {
      grid-template-columns: 1fr;
    }
  }

  .championship-group {
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  .championship-group-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 16px 10px 14px;
    background: linear-gradient(90deg, rgba(114, 46, 209, 0.07) 0%, rgba(255, 255, 255, 0) 100%);
    border: 1px solid rgba(114, 46, 209, 0.18);
    border-radius: 8px 8px 0 0;
    border-bottom: none;
  }

  .championship-group-name {
    font-weight: 700;
    font-size: 14px;
    color: #101242;
    flex: 1 1 auto;
    min-width: 0;
  }

  .championship-sessions-list {
    display: flex;
    flex-direction: column;
    gap: 0;
    padding-left: 16px;
    border-left: 3px solid rgba(114, 46, 209, 0.28);
    border-bottom: 3px solid rgba(114, 46, 209, 0.28);
    border-right: 3px solid rgba(114, 46, 209, 0.28);
    border-radius: 0 0 8px 8px;
  }

  .championship-session-item {
    border-top: 1px solid rgba(114, 46, 209, 0.10);
  }

  .championship-session-item:first-child {
    border-top: none;
  }
`

const PlaceholderSeasonDisplay = (): JSX.Element => {
  return (
    <StyledSeasonDisplay>
      <Skeleton paragraph={{ rows: 0 }} active />
      <div className="swim-meets">
        <div className="swim-meet">
          <Skeleton.Button active />
          <Skeleton paragraph={{ rows: 4 }} active />
        </div>
        <div className="swim-meet">
          <Skeleton.Button active />
          <Skeleton paragraph={{ rows: 4 }} active />
        </div>
      </div>
    </StyledSeasonDisplay>
  )
}

interface IRemovedMeet {
  meetId: number
  meetName: string
  meet_date: string
  meetstatus: number | null
  removedAt: string | null
  removedByUserId: number | null
  removedByClubId: number | null
  removedByNameSnapshot: string | null
  removedByEmailSnapshot: string | null
  removedByClubNameSnapshot: string | null
}

interface IVisibleVirtualMeet {
  virtualMeetInvitationId: number
  virtualMeetId: number
  clubId: number
  status: "accepted" | "pending" | "declined"
  seasonId: number | null
  seasonName: string | null
  poolId: number | null
  acceptedMeetId: number | null
  meetstatus: number | null
  meetName: string
  meetdate: string
  startdate: string | null
  meetTime: string | null
  course: number
  organizerClubId: number
  organizerClubName: string | null
  organizerClubNameLong: string | null
  pendingInvitations: number
  acceptedInvitations: number
  declinedInvitations: number
  removedInvitations: number
}

interface IVirtualMeetParticipantOverview {
  virtualMeetInvitationId: number
  virtualMeetId: number
  clubId: number
  status: "accepted" | "pending" | "declined" | "removed"
  seasonId: number | null
  seasonName: string | null
  poolId: number | null
  acceptedMeetId: number | null
  clubName: string | null
  clubNameLong: string | null
  leagueName: string | null
  meetstatus: number | null
  latestPublishedAt?: string | null
  publishCount?: number
}

interface IVirtualMeetParticipantOverviewResponse {
  virtualMeetId: number
  participants: IVirtualMeetParticipantOverview[]
}

type TVirtualMeetStatusBucket = "pending" | "accepted" | "declined" | "removed"

const formatCourseLabel = (course: number | null | undefined): string => {
  if (course === 1) return "SCM"
  if (course === 2) return "LCM"
  return "SCY"
}

function VirtualMeetInvitationSection({
  invitations,
  onReviewInvitation,
}: {
  invitations: IVisibleVirtualMeet[]
  onReviewInvitation: (invitation: IVisibleVirtualMeet) => void
}): JSX.Element | null {
  if (invitations.length === 0) {
    return null
  }

  return (
    <StyledSeasonDisplay className="shared-virtual-shell">
      <Card className="shared-virtual-summary-card" variant="borderless">
        <div className="shared-virtual-summary-header">
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", color: "#1677ff", marginBottom: 6 }}>
              Needs Response
            </div>
            <h2 className="shared-virtual-summary-title">Pending Virtual Meet Invitations</h2>
          </div>
          <div className="shared-virtual-count">{invitations.length} Invitation{invitations.length === 1 ? "" : "s"}</div>
        </div>
        <div className="shared-virtual-summary-copy">
          Review invitations here. A virtual meet moves into a season only after your club accepts it and chooses a season roster.
        </div>
        <div className="shared-virtual-grid">
        {invitations.map((invitation) => (
          <SharedVirtualMeetInvitationCard
            key={`virtual_invitation_${invitation.virtualMeetInvitationId}`}
            invitation={invitation}
            onReviewInvitation={() => onReviewInvitation(invitation)}
          />
        ))}
        </div>
      </Card>
    </StyledSeasonDisplay>
  )
}

const getLegStatusMeta = (meetStatus: number | null | undefined) => {
  if (meetStatus === 2) {
    return { label: "Results Published", color: "#52c41a" }
  }

  if (meetStatus === 1) {
    return { label: "Heat Sheet Merged", color: "#1677ff" }
  }

  return { label: "Editing Entries", color: "#faad14" }
}

const getParticipantDisplayName = (
  participant: IVirtualMeetParticipantOverview,
): string =>
  participant.clubNameLong ||
  participant.clubName ||
  `Club ${participant.clubId}`

const getMyClubStateCopy = (
  status: IVirtualMeetParticipantOverview["status"],
): string => {
  if (status === "accepted") {
    return "Your club's leg of the meet is active in this virtual meet."
  }

  if (status === "pending") {
    return "Your club has been invited but has not completed setup yet."
  }

  if (status === "declined") {
    return "Your club previously declined this virtual meet invitation."
  }

  return "Your club was removed from this virtual meet."
}

const getStatusBucketMeta = (bucket: TVirtualMeetStatusBucket) => {
  if (bucket === "accepted") {
    return {
      title: "Accepted Teams",
      tagColor: "green",
      borderColor: "rgba(82, 196, 26, 0.28)",
      background: "linear-gradient(180deg, rgba(246,255,237,0.95) 0%, rgba(255,255,255,0.98) 100%)",
      emptyText: "No teams have accepted this virtual meet yet.",
    }
  }

  if (bucket === "pending") {
    return {
      title: "Pending",
      tagColor: "blue",
      borderColor: "rgba(22, 119, 255, 0.28)",
      background: "linear-gradient(180deg, rgba(230,244,255,0.95) 0%, rgba(255,255,255,0.98) 100%)",
      emptyText: "No clubs are currently pending.",
    }
  }

  if (bucket === "declined") {
    return {
      title: "Declined",
      tagColor: "default",
      borderColor: "rgba(140, 140, 140, 0.24)",
      background: "linear-gradient(180deg, rgba(245,245,245,0.96) 0%, rgba(255,255,255,0.98) 100%)",
      emptyText: "No clubs have declined.",
    }
  }

  return {
    title: "Removed",
    tagColor: "orange",
    borderColor: "rgba(250, 140, 22, 0.24)",
    background: "linear-gradient(180deg, rgba(255,247,230,0.96) 0%, rgba(255,255,255,0.98) 100%)",
    emptyText: "No clubs have been removed.",
  }
}

const orderAcceptedParticipants = ({
  participants,
  organizerClubId,
  activeClubId,
}: {
  participants: IVirtualMeetParticipantOverview[]
  organizerClubId: number
  activeClubId: number | null
}): IVirtualMeetParticipantOverview[] => {
  const alphabeticalParticipants = [...participants].sort((left, right) =>
    getParticipantDisplayName(left).localeCompare(getParticipantDisplayName(right)),
  )

  const organizerParticipant = alphabeticalParticipants.find(
    (participant) => participant.clubId === organizerClubId,
  )
  const myParticipant =
    activeClubId === null
      ? undefined
      : alphabeticalParticipants.find(
          (participant) => participant.clubId === activeClubId,
        )

  const prioritizedClubIds = new Set<number>()
  const orderedParticipants: IVirtualMeetParticipantOverview[] = []

  if (organizerParticipant) {
    orderedParticipants.push(organizerParticipant)
    prioritizedClubIds.add(organizerParticipant.clubId)
  }

  if (myParticipant && !prioritizedClubIds.has(myParticipant.clubId)) {
    orderedParticipants.push(myParticipant)
    prioritizedClubIds.add(myParticipant.clubId)
  }

  return orderedParticipants.concat(
    alphabeticalParticipants.filter(
      (participant) => !prioritizedClubIds.has(participant.clubId),
    ),
  )
}

const shouldDefaultTeamSectionExpanded = ({
  viewMode,
}: {
  teamCount: number
  viewMode: TSeasonViewMode
}): boolean => viewMode !== "compact"

const getCompactVirtualSummary = ({
  acceptedCount,
  pendingCount,
  declinedCount,
  removedCount,
}: {
  acceptedCount: number
  pendingCount: number
  declinedCount: number
  removedCount: number
}): string => {
  const summaryParts = [`${acceptedCount} accepted`]

  if (pendingCount > 0) {
    summaryParts.push(`${pendingCount} pending`)
  }

  if (declinedCount > 0) {
    summaryParts.push(`${declinedCount} declined`)
  }

  if (removedCount > 0) {
    summaryParts.push(`${removedCount} removed`)
  }

  return summaryParts.join(", ")
}

function AcceptedVirtualMeetSection({
  virtualMeets,
  participantMap,
  loadingParticipants,
  activeClubId,
  onReviewInvitation,
  onManageShell,
  onOpenMeet,
  onOpenVirtualResults,
  viewMode,
}: {
  virtualMeets: IVisibleVirtualMeet[]
  participantMap: Record<number, IVirtualMeetParticipantOverview[]>
  loadingParticipants: boolean
  activeClubId: number | null
  onReviewInvitation: (invitation: IVisibleVirtualMeet) => void
  onManageShell: (virtualMeetId: number) => void
  onOpenMeet: (meetId: number) => void
  onOpenVirtualResults: (virtualMeetId: number) => void
  viewMode: TSeasonViewMode
}): JSX.Element | null {
  const [statusModalVirtualMeetId, setStatusModalVirtualMeetId] = useState<number | null>(null)
  const [focusedStatusBucket, setFocusedStatusBucket] =
    useState<TVirtualMeetStatusBucket>("accepted")
  const [resultsInfoVirtualMeetId, setResultsInfoVirtualMeetId] = useState<number | null>(null)
  const [expandedAcceptedLegSections, setExpandedAcceptedLegSections] = useState<
    Record<number, boolean>
  >({})

  useEffect(() => {
    setExpandedAcceptedLegSections({})
  }, [viewMode])

  if (virtualMeets.length === 0) {
    return null
  }

  const statusModalVirtualMeet =
    statusModalVirtualMeetId === null
      ? null
      : virtualMeets.find(
          (virtualMeet) => virtualMeet.virtualMeetId === statusModalVirtualMeetId,
        ) || null
  const statusModalParticipants =
    statusModalVirtualMeetId === null
      ? []
      : participantMap[statusModalVirtualMeetId] || []
  const resultsInfoVirtualMeet =
    resultsInfoVirtualMeetId === null
      ? null
      : virtualMeets.find(
          (virtualMeet) => virtualMeet.virtualMeetId === resultsInfoVirtualMeetId,
        ) || null

  const openStatusModal = (
    virtualMeetId: number,
    bucket: TVirtualMeetStatusBucket,
  ) => {
    setStatusModalVirtualMeetId(virtualMeetId)
    setFocusedStatusBucket(bucket)
  }

  const renderStatusBucket = (
    bucket: TVirtualMeetStatusBucket,
    participants: IVirtualMeetParticipantOverview[],
  ) => {
    const bucketMeta = getStatusBucketMeta(bucket)
    const isFocusedBucket = focusedStatusBucket === bucket

    return (
      <div
        key={`status_bucket_${bucket}`}
        style={{
          borderRadius: 14,
          border: isFocusedBucket
            ? `2px solid ${bucketMeta.borderColor.replace("0.28", "0.7").replace("0.24", "0.7")}`
            : `1px solid ${bucketMeta.borderColor}`,
          background: bucketMeta.background,
          padding: 14,
          boxShadow: isFocusedBucket
            ? "0 10px 24px rgba(16, 18, 66, 0.10)"
            : "none",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 10,
            marginBottom: 10,
          }}
        >
          <div style={{ fontWeight: 700, color: "#101242" }}>{bucketMeta.title}</div>
          <Tag color={bucketMeta.tagColor}>{participants.length}</Tag>
        </div>
        {participants.length === 0 ? (
          <div style={{ fontSize: 12, color: "rgba(16, 18, 66, 0.62)" }}>
            {bucketMeta.emptyText}
          </div>
        ) : (
          <div style={{ display: "grid", gap: 8 }}>
            {participants.map((participant) => {
              const isMyClub = participant.clubId === activeClubId
              const isOrganizerClub =
                participant.clubId === statusModalVirtualMeet?.organizerClubId

              return (
                <div
                  key={`status_bucket_row_${bucket}_${participant.virtualMeetInvitationId}`}
                  style={{
                    borderRadius: 10,
                    border: "1px solid rgba(16, 18, 66, 0.08)",
                    background: "rgba(255,255,255,0.82)",
                    padding: "8px 10px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 10,
                      flexWrap: "wrap",
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 700, color: "#101242" }}>
                        {getParticipantDisplayName(participant)}
                        {isMyClub ? (
                          <Tag color="geekblue" style={{ marginLeft: 8 }}>
                            My Club
                          </Tag>
                        ) : null}
                        {isOrganizerClub ? (
                          <Tag color="gold" style={{ marginLeft: 8 }}>
                            Organizer
                          </Tag>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  return (
    <>
      <div
        className={`shared-virtual-grid season-accepted-virtual-grid${viewMode === "compact" ? " season-accepted-virtual-grid-compact" : ""}`}
      >
        {virtualMeets.map((virtualMeet) => {
          const participants = participantMap[virtualMeet.virtualMeetId] || []
          const acceptedParticipants = participants.filter(
            (participant) => participant.status === "accepted",
          )
          const orderedAcceptedParticipants = orderAcceptedParticipants({
            participants: acceptedParticipants,
            organizerClubId: virtualMeet.organizerClubId,
            activeClubId,
          })
          const pendingParticipants = participants.filter(
            (participant) => participant.status === "pending",
          )
          const declinedParticipants = participants.filter(
            (participant) => participant.status === "declined",
          )
          const removedParticipants = participants.filter(
            (participant) => participant.status === "removed",
          )
          const hasVirtualResults = acceptedParticipants.some(
            (participant) => participant.meetstatus === 2,
          )
          const myPendingParticipant = pendingParticipants.find(
            (participant) => participant.clubId === activeClubId,
          )
          const myDeclinedParticipant = declinedParticipants.find(
            (participant) => participant.clubId === activeClubId,
          )
          const myRemovedParticipant = removedParticipants.find(
            (participant) => participant.clubId === activeClubId,
          )
          const otherDeclinedParticipants = declinedParticipants.filter(
            (participant) => participant.clubId !== activeClubId,
          )
          const isOrganizer =
            activeClubId !== null && virtualMeet.organizerClubId === activeClubId
          const organizerName =
            virtualMeet.organizerClubNameLong || virtualMeet.organizerClubName
          const acceptedLegsExpanded =
            expandedAcceptedLegSections[virtualMeet.virtualMeetId] ??
            shouldDefaultTeamSectionExpanded({
              teamCount: acceptedParticipants.length,
              viewMode,
            })
          const compactSummary = getCompactVirtualSummary({
            acceptedCount: virtualMeet.acceptedInvitations,
            pendingCount: virtualMeet.pendingInvitations,
            declinedCount: virtualMeet.declinedInvitations,
            removedCount: virtualMeet.removedInvitations,
          })

          return viewMode === "compact" ? (
            <Card
              key={`virtual_shared_${virtualMeet.virtualMeetInvitationId}`}
              className="shared-virtual-meet-card season-accepted-virtual-card shared-virtual-meet-card-compact"
              variant="borderless"
              onClick={
                Number.isInteger(virtualMeet.acceptedMeetId)
                  ? () => onOpenMeet(virtualMeet.acceptedMeetId as number)
                  : undefined
              }
              style={{ cursor: "pointer" }}
            >
              <div className="virtual-card-header">
                <div style={{ flex: "1 1 320px", minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", color: "#595959", marginBottom: 4 }}>
                    Virtual Meet
                  </div>
                  <h3 className="virtual-card-title">{virtualMeet.meetName}</h3>
                  <h4 className="virtual-card-meta">
                    {virtualMeet.startdate ? (
                      <span>
                        Meet Window: {dayjs(virtualMeet.startdate).format("MM-DD-YYYY")} to {dayjs(virtualMeet.meetdate).format("MM-DD-YYYY")}
                      </span>
                    ) : (
                      <span>{dayjs(virtualMeet.meetdate).format("MM-DD-YYYY")}</span>
                    )}
                    <br />
                    <span>Virtual Course: {formatCourseLabel(virtualMeet.course)}</span>
                    {organizerName ? (
                      <span><br />Organizer: {organizerName}</span>
                    ) : null}
                  </h4>
                </div>
                {isOrganizer ? (
                  <Tooltip title="Manage virtual meet">
                    <button
                      type="button"
                      className="virtual-card-manage-button"
                      onClick={(event) => {
                        event.stopPropagation()
                        onManageShell(virtualMeet.virtualMeetId)
                      }}
                    >
                      <FontAwesomeIcon icon={faGear} />
                    </button>
                  </Tooltip>
                ) : null}
              </div>
              <div className="season-compact-summary">
                <span>
                  <strong>Teams</strong> {compactSummary}
                </span>
                <span>{hasVirtualResults ? "Results ready" : "Results pending"}</span>
              </div>
              <div className="season-compact-card-footer">
                <span>Tap to open your meet workspace.</span>
                <span className="season-compact-actions">
                  {hasVirtualResults ? (
                    <Button
                      size="small"
                      type="link"
                      onClick={(event) => {
                        event.stopPropagation()
                        onOpenVirtualResults(virtualMeet.virtualMeetId)
                      }}
                      style={{ padding: 0 }}
                    >
                      Results
                    </Button>
                  ) : null}
                </span>
              </div>
            </Card>
          ) : (
            <SharedAcceptedVirtualMeetCard
              key={`virtual_shared_${virtualMeet.virtualMeetInvitationId}`}
              virtualMeet={virtualMeet}
              participants={participants}
              loadingParticipants={loadingParticipants}
              activeClubId={activeClubId}
              acceptedLegsExpanded={acceptedLegsExpanded}
              onToggleAcceptedLegs={() =>
                setExpandedAcceptedLegSections((current) => ({
                  ...current,
                  [virtualMeet.virtualMeetId]: !acceptedLegsExpanded,
                }))
              }
              onOpenMeet={onOpenMeet}
              onManageShell={isOrganizer ? onManageShell : undefined}
              onOpenVirtualResults={onOpenVirtualResults}
              onOpenStatusBucket={openStatusModal}
            />
          )
        })}
      </div>
      <Modal
        title={
          resultsInfoVirtualMeet
            ? `${resultsInfoVirtualMeet.meetName} Results Info`
            : "Virtual Meet Results Info"
        }
        open={resultsInfoVirtualMeetId !== null}
        onCancel={() => setResultsInfoVirtualMeetId(null)}
        footer={null}
        width={520}
      >
        <div style={{ fontSize: 14, lineHeight: 1.6, color: "rgba(16, 18, 66, 0.82)" }}>
          Overall virtual meet results update automatically as each participating club publishes the results for its accepted leg of the meet.
          <br />
          <br />
          {resultsInfoVirtualMeet && resultsInfoVirtualMeet.acceptedInvitations > 0
            ? "As more teams publish their results, the virtual meet results become more complete."
            : "Virtual meet results will appear once accepted teams begin publishing their meet results."}
        </div>
      </Modal>
      <Modal
        title={
          statusModalVirtualMeet
            ? `${statusModalVirtualMeet.meetName} Status Overview`
            : "Virtual Meet Status Overview"
        }
        open={statusModalVirtualMeetId !== null}
        onCancel={() => setStatusModalVirtualMeetId(null)}
        footer={null}
        width={920}
      >
        <div
          className="virtual-meet-status-overview-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
            gap: 12,
          }}
        >
          {renderStatusBucket(
            "accepted",
            orderAcceptedParticipants({
              participants: statusModalParticipants.filter(
                (participant) => participant.status === "accepted",
              ),
              organizerClubId: statusModalVirtualMeet?.organizerClubId || 0,
              activeClubId,
            }),
          )}
          {renderStatusBucket(
            "pending",
            statusModalParticipants.filter(
              (participant) => participant.status === "pending",
            ),
          )}
          {renderStatusBucket(
            "declined",
            statusModalParticipants.filter(
              (participant) => participant.status === "declined",
            ),
          )}
          {renderStatusBucket(
            "removed",
            statusModalParticipants.filter(
              (participant) => participant.status === "removed",
            ),
          )}
        </div>
        <style>
          {`
            @media (max-width: 700px) {
              .virtual-meet-status-overview-grid {
                grid-template-columns: 1fr !important;
              }
            }
          `}
        </style>
      </Modal>
    </>
  )
}

function OrganizerVirtualMeetShellSection({
  shells,
  pendingInvitations,
  onReviewInvitation,
  onManageShell,
}: {
  shells: IVisibleVirtualMeet[]
  pendingInvitations: IVisibleVirtualMeet[]
  onReviewInvitation: (invitation: IVisibleVirtualMeet) => void
  onManageShell: (virtualMeetId: number) => void
}): JSX.Element | null {
  if (shells.length === 0) {
    return null
  }

  return (
    <StyledSeasonDisplay>
      <h2 className="season-title">
        <span>Virtual Meets</span>
      </h2>
      <div className="swim-meets">
        {shells.map((shell) => {
          const myPendingInvitation = pendingInvitations.find(
            (invitation) => invitation.virtualMeetId === shell.virtualMeetId,
          )

          return (
            <div key={`virtual_shell_${shell.virtualMeetId}`} className="swim-meet">
              <h3>{shell.meetName}</h3>
              <h4>
                {shell.startdate ? (
                  <span>
                    Meet Window: {dayjs(shell.startdate).format("MM-DD-YYYY")} to {dayjs(shell.meetdate).format("MM-DD-YYYY")}
                  </span>
                ) : (
                  <span>{dayjs(shell.meetdate).format("MM-DD-YYYY")}</span>
                )}
                <br />
                <span>Virtual Course: {formatCourseLabel(shell.course)}</span>
              </h4>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
                <Tag color="blue">Pending {shell.pendingInvitations}</Tag>
                <Tag color="green">Accepted {shell.acceptedInvitations}</Tag>
                <Tag color="default">Declined {shell.declinedInvitations}</Tag>
                <Tag color="orange">Removed {shell.removedInvitations}</Tag>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {myPendingInvitation ? (
                  <Button type="primary" onClick={() => onReviewInvitation(myPendingInvitation)}>
                    Complete My Club Setup
                  </Button>
                ) : (
                  <Tooltip title="Your club has already accepted this virtual meet or no response is currently needed.">
                    <Button disabled>My Club Setup Complete</Button>
                  </Tooltip>
                )}
                <Button onClick={() => onManageShell(shell.virtualMeetId)}>
                  Manage Virtual Meet
                </Button>
              </div>
            </div>
          )
        })}
      </div>
    </StyledSeasonDisplay>
  )
}

function ClubSeasonMeets({
  clubSeasonId,
  seasonEnded,
  openCreateMeetModal,
  setMeetIdToEdit,
  setEditMeetModalVisible,
  triggerRefresh,
  viewMode,
  sortDirection,
  acceptedVirtualMeets,
  participantMap,
  loadingVirtualMeetParticipants,
  activeClubId,
  onReviewInvitation,
  onManageShell,
  onOpenMeet,
  onOpenVirtualResults,
  onOpenRemovedMeet,
  onOpenStandings,
}: {
  clubSeasonId: number
  seasonEnded: boolean
  openCreateMeetModal: () => void
  setMeetIdToEdit: (meetIdToEdit: number) => void
  setEditMeetModalVisible: (newVisible: boolean) => void
  triggerRefresh: number
  viewMode: TSeasonViewMode
  sortDirection: TSeasonSortDirection
  acceptedVirtualMeets: IVisibleVirtualMeet[]
  participantMap: Record<number, IVirtualMeetParticipantOverview[]>
  loadingVirtualMeetParticipants: boolean
  activeClubId: number | null
  onReviewInvitation: (invitation: IVisibleVirtualMeet) => void
  onManageShell: (virtualMeetId: number) => void
  onOpenMeet: (meetId: number) => void
  onOpenVirtualResults: (virtualMeetId: number) => void
  onOpenRemovedMeet: (meetId: number) => void
  onOpenStandings: (msmId: number, name: string, clubs: Array<{ clubId: number; clubName: string; clubNameLong?: string | null }>) => void
}): JSX.Element {
  const [meets, setMeets] = useState<ISeasonMeetWithClubs[]>([])
  const [removedMeets, setRemovedMeets] = useState<IRemovedMeet[]>([])
  const [loading, setLoading] = useState(false)
  const [activeSeasonSection, setActiveSeasonSection] =
    useState<TSeasonMeetSection>("available")
  const [expandedMeetTeamSections, setExpandedMeetTeamSections] = useState<
    Record<number, boolean>
  >({})
  const user = useStoreState((state: StateMapper<AppDataStore>) => state.user)
  const [canUseMagicWizard, setCanUseMagicWizard] = useState(false)

  useEffect(() => {
    if (user?.role !== 2) {
      setCanUseMagicWizard(false)
      return
    }
    swimminglyApi
      .get(gen("/api/admin/swimmingly-panel-permissions/check/magic-wizard"))
      .then((data: any) => {
        setCanUseMagicWizard(data?.hasAccess === true)
      })
      .catch(() => setCanUseMagicWizard(false))
  }, [user?.role, user?.userId])

  const isSwimminglyAdmin = canUseMagicWizard

  useEffect(() => {
    setExpandedMeetTeamSections({})
  }, [viewMode])

  useEffect(() => {
    if (activeSeasonSection === "removed" && !visibleRemovedMeets.length) {
      setActiveSeasonSection("available")
    }
  }, [activeSeasonSection, removedMeets.length, seasonEnded])

  const getMeets = useCallback(() => {
    let mounted = true
    if (!clubSeasonId) {
      if (mounted) {
        setMeets([])
        setRemovedMeets([])
      }
      return
    }
    if (mounted) {
      setLoading(true)
    }
    swimminglyApi
      .get(gen(`/api/getClubSeasonMeets/${clubSeasonId}`))
      .then(
        ({
          status,
          meets,
          removedMeets,
        }: {
          status: string
          meets: ISeasonMeetWithClubs[]
          removedMeets?: IRemovedMeet[]
        }) => {
          if (status === "success") {
            if (mounted) {
              setMeets(meets)
              setRemovedMeets(removedMeets || [])
            }
          } else {
            message.error("Problem finding meets for season")
          }
        },
      )
      .finally(() => {
        if (mounted) {
          setLoading(false)
        }
      })
    return () => {
      mounted = false
    }
  }, [clubSeasonId, setMeets, setLoading])

  // now we can trigger this component to update its list of meets from any other component
  // using our event emitter
  useEffect(() => {
    updateSeasonMeetArrayListener.on(
      `updateSeasonMeets${clubSeasonId}`,
      getMeets,
    )
    return () =>
      updateSeasonMeetArrayListener.removeAllListeners(
        `updateSeasonMeets${clubSeasonId}`,
      )
  }, [clubSeasonId, getMeets])

  useEffect(getMeets, [getMeets, triggerRefresh])

  if (loading) {
    return (
      <div className="swim-meets">
        <div className="swim-meet">
          <Skeleton.Button active />
          <Skeleton paragraph={{ rows: 4 }} active />
        </div>
        <div className="swim-meet">
          <Skeleton.Button active />
          <Skeleton paragraph={{ rows: 4 }} active />
        </div>
      </div>
    )
  }

  const acceptedMeetIds = new Set(
    acceptedVirtualMeets
      .map((virtualMeet) => virtualMeet.acceptedMeetId)
      .filter((acceptedMeetId): acceptedMeetId is number =>
        Number.isInteger(acceptedMeetId),
      ),
  )

  const seasonAcceptedVirtualMeets = acceptedVirtualMeets.filter((virtualMeet) =>
    Number.isInteger(virtualMeet.acceptedMeetId) &&
    meets.some((meet) => meet.meet.meetId === virtualMeet.acceptedMeetId),
  )

  const displayedMeets = [...meets]
    .filter((meet) => !acceptedMeetIds.has(meet.meet.meetId))
    .sort((left, right) => {
      const leftSortDate = dayjs(left.startdate || left.meet.meet_date).valueOf()
      const rightSortDate = dayjs(right.startdate || right.meet.meet_date).valueOf()

      return sortDirection === "desc"
        ? rightSortDate - leftSortDate
        : leftSortDate - rightSortDate
    })

  const visibleRemovedMeets = !seasonEnded ? removedMeets : []
  const hasAvailableMeets =
    displayedMeets.length > 0 || seasonAcceptedVirtualMeets.length > 0
  const hasRemovedMeets = visibleRemovedMeets.length > 0

  if (
    !hasAvailableMeets &&
    !hasRemovedMeets
  ) {
    return (
      <div className="swim-meets">
        <Empty
          style={!seasonEnded ? { cursor: "pointer" } : {}}
          image={swimminglyImage}
          className="empty-image"
          description={
            seasonEnded ? (
              <span>There were no meets this season</span>
            ) : (
              <span>
                There aren't any meets for this season yet!{" "}
                <Button
                  onClick={openCreateMeetModal}
                  type="link"
                  style={{ padding: 0 }}
                >
                  Add a Swim Meet
                </Button>
              </span>
            )
          }
        />
      </div>
    )
  }

  return (
    <>
      <div className="season-section-tabs">
        <button
          type="button"
          className={`season-section-tab${activeSeasonSection === "available" ? " active" : ""}`}
          onClick={() => setActiveSeasonSection("available")}
        >
          Available Meets ({displayedMeets.length + seasonAcceptedVirtualMeets.length})
        </button>
        <button
          type="button"
          className={`season-section-tab${activeSeasonSection === "removed" ? " active" : ""}`}
          onClick={() => setActiveSeasonSection("removed")}
          disabled={!hasRemovedMeets}
        >
          Removed Meets ({visibleRemovedMeets.length})
        </button>
      </div>
      {activeSeasonSection === "available" && seasonAcceptedVirtualMeets.length > 0 ? (
        <div style={displayedMeets.length > 0 ? { marginBottom: 16 } : undefined}>
          <AcceptedVirtualMeetSection
            virtualMeets={seasonAcceptedVirtualMeets}
            participantMap={participantMap}
            loadingParticipants={loadingVirtualMeetParticipants}
            activeClubId={activeClubId}
            onReviewInvitation={onReviewInvitation}
            onManageShell={onManageShell}
            onOpenMeet={onOpenMeet}
            onOpenVirtualResults={onOpenVirtualResults}
            viewMode={viewMode}
          />
        </div>
      ) : null}
      {activeSeasonSection === "available" && displayedMeets.length > 0 ? (
        <div className={`swim-meets season-meet-rows${viewMode === "compact" ? " season-meet-rows-compact" : ""}`}>
          {(() => {
            // Group meets: championships together, standalone separate.
            // Pre-compute which meetIds are part of any championship group so we can
            // suppress stale "standalone" duplicates that lack multiSessionMeetId.
            const championshipMeetIds = new Set<number>(
              displayedMeets
                .filter((m) => m.meet.multiSessionMeetId != null)
                .map((m) => m.meet.meetId),
            )
            const renderedChampionshipIds = new Set<number>()
            return displayedMeets.map((meet, index) => {
              const msid = meet.meet.multiSessionMeetId
              if (msid) {
                if (renderedChampionshipIds.has(msid)) return null
                renderedChampionshipIds.add(msid)
                const sessionMeets = displayedMeets
                  .filter((m) => m.meet.multiSessionMeetId === msid)
                  .sort((a, b) => (a.meet.sessionOrder ?? 0) - (b.meet.sessionOrder ?? 0))
                const sessionCount = sessionMeets.length
                const dates = sessionMeets.map((m) => dayjs(m.meet.meet_date))
                const dateRange =
                  sessionCount > 1
                    ? `${dates[0].format("MMM D")}–${dates[sessionCount - 1].format("MMM D, YYYY")}`
                    : dates[0].format("MMM D, YYYY")
                return (
                  <div key={`championship_group_${msid}`} className="championship-group">
                    <div className="championship-group-header">
                      <Tag color="purple" style={{ marginInlineEnd: 0, flexShrink: 0 }}>
                        Multi-Session Swim Meet
                      </Tag>
                      <span className="championship-group-name">
                        {meet.meet.multiSessionMeetName || "Multi-Session Championship"}
                      </span>
                      <span style={{ fontSize: 12, color: "rgba(16, 18, 66, 0.55)", flexShrink: 0 }}>
                        {sessionCount} session{sessionCount !== 1 ? "s" : ""} · {dateRange}
                      </span>
                      <Button
                        type="link"
                        size="small"
                        style={{ marginLeft: "auto", flexShrink: 0, padding: "0 4px" }}
                        onClick={() => {
                          const allClubs = Array.from(
                            new Map(
                              sessionMeets.flatMap((sm) =>
                                sm.clubs.map((c) => [
                                  c.club.clubId,
                                  { clubId: c.club.clubId!, clubName: c.club.name ?? "", clubNameLong: c.club.name_long ?? null },
                                ]),
                              ),
                            ).values(),
                          )
                          onOpenStandings(msid, meet.meet.multiSessionMeetName || "Championship", allClubs)
                        }}
                      >
                        Standings
                      </Button>
                    </div>
                    <div className="championship-sessions-list">
                      {sessionMeets.map((sessionMeet, si) => {
                        const teamsExpanded =
                          expandedMeetTeamSections[sessionMeet.meet.meetId] ??
                          shouldDefaultTeamSectionExpanded({
                            teamCount: sessionMeet.clubs.length,
                            viewMode,
                          })
                        return (
                          <div key={`session_item_${sessionMeet.meet.meetId}`} className="championship-session-item">
                            <SeasonMeetCard
                              meet={sessionMeet}
                              viewMode={viewMode}
                              teamsExpanded={teamsExpanded}
                              myClubId={activeClubId}
                              isSwimminglyAdmin={isSwimminglyAdmin}
                              seasonEnded={seasonEnded}
                              onRefresh={getMeets}
                              onOpenMeet={() => {
                                setMeetIdToEdit(sessionMeet.meet.meetId)
                                setEditMeetModalVisible(true)
                              }}
                              onToggleTeams={() => {
                                setExpandedMeetTeamSections((current) => ({
                                  ...current,
                                  [sessionMeet.meet.meetId]: !teamsExpanded,
                                }))
                              }}
                            />
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )
              }
              // Suppress any standalone duplicate for a meet that belongs to a championship group
              if (championshipMeetIds.has(meet.meet.meetId)) return null
              const teamsExpanded =
                expandedMeetTeamSections[meet.meet.meetId] ??
                shouldDefaultTeamSectionExpanded({
                  teamCount: meet.clubs.length,
                  viewMode,
                })
              return (
                <SeasonMeetCard
                  key={`meet_card_${meet.meet.meetId}_${index}`}
                  meet={meet}
                  viewMode={viewMode}
                  teamsExpanded={teamsExpanded}
                  myClubId={activeClubId}
                  isSwimminglyAdmin={isSwimminglyAdmin}
                  seasonEnded={seasonEnded}
                  onRefresh={getMeets}
                  onOpenMeet={() => {
                    setMeetIdToEdit(meet.meet.meetId)
                    setEditMeetModalVisible(true)
                  }}
                  onToggleTeams={() => {
                    setExpandedMeetTeamSections((current) => ({
                      ...current,
                      [meet.meet.meetId]: !teamsExpanded,
                    }))
                  }}
                />
              )
            }).filter(Boolean)
          })()}
        </div>
      ) : null}
      {activeSeasonSection === "available" && !hasAvailableMeets ? (
        <div className="season-section-empty">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No available meets in this season right now."
          />
        </div>
      ) : null}
      {activeSeasonSection === "removed" ? (
        hasRemovedMeets ? (
          <div className={`swim-meets season-meet-rows${viewMode === "compact" ? " season-meet-rows-compact" : ""}`}>
            {visibleRemovedMeets.map((meet) => (
              <div
                key={`removed_meet_card_${meet.meetId}`}
                className={`swim-meet season-meet-card season-removed-card${viewMode === "compact" ? " season-meet-card-compact" : ""}`}
                onClick={() => onOpenRemovedMeet(meet.meetId)}
              >
                <div className="season-meet-row-header">
                  <div className="season-meet-row-title">
                    <h3>{meet.meetName}</h3>
                    <div className="season-meet-meta">
                      <span>{dayjs(meet.meet_date).format("MM-DD-YYYY")}</span>
                    </div>
                  </div>
                  <div className="season-status-summary">
                    <Tag color="default">Removed</Tag>
                  </div>
                </div>
                <div className="season-removed-meta">
                  <div>
                    Removed {meet.removedAt ? dayjs(meet.removedAt).format("MM-DD-YYYY h:mm A") : "recently"}
                  </div>
                  <div>
                    By {meet.removedByNameSnapshot || "Unknown administrator"}
                  </div>
                  <div>
                    Club affiliation: {meet.removedByClubNameSnapshot || "Unavailable"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="season-section-empty">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No removed meets in this season."
            />
          </div>
        )
      ) : null}
    </>
  )
}

interface IClubSeasonDisplay {
  visible: boolean
  clubSeason: IClubSeason
}

type TSeasonViewMode = "regular" | "compact"
type TSeasonSortDirection = "asc" | "desc"
type TSeasonMeetSection = "available" | "removed"

const getSeasonHeaderMeta = ({
  startDate,
  endDate,
}: {
  startDate: string
  endDate: string
}) => {
  const now = dayjs()
  const seasonStart = dayjs(startDate)
  const seasonEnd = dayjs(endDate)

  if (seasonEnd.isBefore(now, "day")) {
    return { label: "Ended", color: "default" as const, statusKey: "ended" }
  }

  if (seasonStart.isAfter(now, "day")) {
    return { label: "Upcoming", color: "gold" as const, statusKey: "upcoming" }
  }

  return { label: "In Season", color: "blue" as const, statusKey: "active" }
}

function MeetSchedulePageInner(): JSX.Element {
  const history = useHistory()
  const location = useLocation()
  const impersonateClub = useStoreState(
    (state: StateMapper<AppDataStore>) => state.aliasedClub,
  )
  const { isFeatureLocked, openLockedModal } = useFeatureAccess()

  const [slideIn, setSlideIn] = useState<boolean>(
    () => !!(location.state as Record<string, unknown>)?.backSlide,
  )

  useEffect(() => {
    if (!slideIn) return
    // Clear the history state so a hard refresh doesn't replay the animation
    history.replace({ pathname: location.pathname, search: location.search }, {})
    const timer = setTimeout(() => setSlideIn(false), 400)
    return () => clearTimeout(timer)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [clubSeasons, setClubSeasons] = useState<IClubSeasonDisplay[]>([])
  const [loadingClubSeasons, setLoadingClubSeasons] = useState(false)
  const [selectedSeason, setSelectedSeason] =
    useState<{
      clubSeasonId: number
      seasonId: number
      seasonName: string
      startDate: Dayjs
      endDate: Dayjs
    } | null>(null)
  const [createMeetModalVisible, setCreateMeetModalVisible] = useState(false)
  const [editMeetModalVisible, setEditMeetModalVisible] = useState(false)
  const [meetIdToEdit, setMeetIdToEdit] = useState(-1)
  const [standingsModalMsmId, setStandingsModalMsmId] = useState<number | null>(null)
  const [standingsModalName, setStandingsModalName] = useState("")
  const [standingsModalClubs, setStandingsModalClubs] = useState<Array<{ clubId: number; clubName: string; clubNameLong?: string | null }>>([])
  const [meetActionsInitialTab, setMeetActionsInitialTab] = useState<
    "reportStudio" | "declarations" | "eventOrder" | "activityLog" | null
  >(null)
  const [removedMeetModalVisible, setRemovedMeetModalVisible] = useState(false)
  const [removedMeetIdToView, setRemovedMeetIdToView] = useState<number | null>(null)
  const [triggerRefresh, setTriggerRefresh] = useState(0)
  const [visibleVirtualMeets, setVisibleVirtualMeets] = useState<IVisibleVirtualMeet[]>([])
  const [virtualMeetParticipants, setVirtualMeetParticipants] = useState<
    Record<number, IVirtualMeetParticipantOverview[]>
  >({})
  const [virtualMeetParticipantsLoading, setVirtualMeetParticipantsLoading] =
    useState(false)
  const [selectedVirtualInvitation, setSelectedVirtualInvitation] = useState<
    IVisibleVirtualMeet | null
  >(null)
  const [virtualInvitationModalVisible, setVirtualInvitationModalVisible] =
    useState(false)
  const [selectedVirtualMeetShellId, setSelectedVirtualMeetShellId] = useState<number | null>(null)
  const [virtualMeetShellEditorVisible, setVirtualMeetShellEditorVisible] =
    useState(false)
  const [seasonViewModeById, setSeasonViewModeById] = useState<
    Record<number, TSeasonViewMode>
  >({})
  const [seasonSortDirectionById, setSeasonSortDirectionById] = useState<
    Record<number, TSeasonSortDirection>
  >({})

  useEffect(() => {
    if (createMeetModalVisible === false) {
      setSelectedSeason(null)
    }
  }, [createMeetModalVisible])

  const getClubSeasons = useCallback(() => {
    if (!impersonateClub?.clubId) {
      setClubSeasons([])
      return
    }

    setLoadingClubSeasons(true)
    swimminglyApi
      .get(gen(`/api/getSeasonsForClub/${impersonateClub.clubId}`))
      .then(
        ({
          status,
          clubSeasons,
        }: {
          status: string
          clubSeasons: IClubSeason[]
        }) => {
          if (status === "success") {
            setClubSeasons(
              clubSeasons?.map((clubSeason) => ({
                visible: dayjs(clubSeason.endDate).diff(dayjs(), "days") > -5,
                clubSeason,
              })) || [],
            )
          } else {
            message.error("Problem finding seasons for your club")
          }
        },
      )
      .finally(() => setLoadingClubSeasons(false))
  }, [impersonateClub?.clubId, setClubSeasons, setLoadingClubSeasons])

  useEffect(getClubSeasons, [getClubSeasons])

  useEffect(() => {
    if (!impersonateClub?.clubId) {
      setVisibleVirtualMeets([])
      setVirtualMeetParticipants({})
      setVirtualMeetParticipantsLoading(false)
      return
    }

    let mounted = true
    const activeClubId = impersonateClub.clubId

    swimminglyApi
      .get(gen(`/api/getVisibleVirtualMeets?clubId=${activeClubId}`))
      .then((virtualMeetData) => {
        if (!mounted) {
          return
        }

        const nextVirtualMeets =
          virtualMeetData?.status === "success"
            ? virtualMeetData.virtualMeets || []
            : []

        setVisibleVirtualMeets(nextVirtualMeets)

        const uniqueVirtualMeetIds: number[] = Array.from(
          new Set(
            nextVirtualMeets.map(
              (virtualMeet: IVisibleVirtualMeet) => virtualMeet.virtualMeetId,
            ),
          ),
        )

        if (uniqueVirtualMeetIds.length === 0) {
          setVirtualMeetParticipants({})
          return
        }

        setVirtualMeetParticipantsLoading(true)

        Promise.all(
          uniqueVirtualMeetIds.map((virtualMeetId) =>
            swimminglyApi
              .get(gen(`/api/getVirtualMeetParticipantsOverview/${virtualMeetId}`))
              .then(
                (data: {
                  status?: string
                  participants?: IVirtualMeetParticipantOverview[]
                }) => ({
                  virtualMeetId,
                  participants:
                    data?.status === "success" ? data.participants || [] : [],
                }),
              ),
          ),
        )
          .then((participantResponses: IVirtualMeetParticipantOverviewResponse[]) => {
            if (!mounted) {
              return
            }

            const nextParticipantMap = participantResponses.reduce(
              (accumulator, response) => {
                accumulator[response.virtualMeetId] = response.participants
                return accumulator
              },
              {} as Record<number, IVirtualMeetParticipantOverview[]>,
            )

            setVirtualMeetParticipants(nextParticipantMap)
          })
          .finally(() => {
            if (mounted) {
              setVirtualMeetParticipantsLoading(false)
            }
          })
      })
      .catch(() => {
        if (mounted) {
          message.error("Problem loading virtual meets.")
        }
      })

    return () => {
      mounted = false
    }
  }, [impersonateClub?.clubId, triggerRefresh])

  const updateClubSeasonVisible = (clubSeasonId: number) => {
    const newClubSeasons = cloneDeep(clubSeasons)
    const clubSeasonToUpdate = newClubSeasons.find(
      (clubSeasonDisplay) =>
        clubSeasonDisplay.clubSeason.clubSeasonId === clubSeasonId,
    )
    if (clubSeasonToUpdate) {
      clubSeasonToUpdate.visible = !clubSeasonToUpdate.visible
    }
    setClubSeasons(newClubSeasons)
  }

  const toggleSeasonViewMode = useCallback((clubSeasonId: number) => {
    setSeasonViewModeById((current) => ({
      ...current,
      [clubSeasonId]: current[clubSeasonId] === "compact" ? "regular" : "compact",
    }))
  }, [])

  const toggleSeasonSortDirection = useCallback((clubSeasonId: number) => {
    setSeasonSortDirectionById((current) => ({
      ...current,
      [clubSeasonId]: current[clubSeasonId] === "desc" ? "asc" : "desc",
    }))
  }, [])

  const openCreateMeetModal = useCallback(
    (
      clubSeasonId: number,
      seasonId: number,
      seasonName: string,
      startDate: Dayjs,
      endDate: Dayjs,
    ): void => {
      setSelectedSeason({
        clubSeasonId,
        seasonId,
        seasonName,
        startDate,
        endDate,
      })
      setCreateMeetModalVisible(true)
    },
    [],
  )

  const refreshSchedule = useCallback(() => {
    setTriggerRefresh((current) => current + 1)
  }, [])

  const clearVirtualInvitationQueryParams = useCallback(() => {
    const searchParams = new URLSearchParams(location.search)
    let changed = false

    if (searchParams.has("virtualInvitationId")) {
      searchParams.delete("virtualInvitationId")
      changed = true
    }

    if (searchParams.has("virtualMeetId")) {
      searchParams.delete("virtualMeetId")
      changed = true
    }

    if (!changed) {
      return
    }

    history.replace({
      pathname: location.pathname,
      search: searchParams.toString() ? `?${searchParams.toString()}` : "",
    })
  }, [history, location.pathname, location.search])

  const openVirtualInvitationModal = useCallback(
    (invitation: IVisibleVirtualMeet) => {
      setSelectedVirtualInvitation(invitation)
      setVirtualInvitationModalVisible(true)
    },
    [],
  )

  useEffect(() => {
    if (!impersonateClub?.clubId) {
      return
    }

    const invitationId = parseInt(
      new URLSearchParams(location.search).get("virtualInvitationId") || "",
      10,
    )

    if (!Number.isInteger(invitationId) || invitationId <= 0) {
      return
    }

    if (
      virtualInvitationModalVisible &&
      selectedVirtualInvitation?.virtualMeetInvitationId === invitationId
    ) {
      return
    }

    const matchingInvitation = visibleVirtualMeets.find(
      (invitation) =>
        invitation.virtualMeetInvitationId === invitationId && invitation.status === "pending",
    )

    if (!matchingInvitation) {
      return
    }

    setSelectedVirtualInvitation(matchingInvitation)
    setVirtualInvitationModalVisible(true)
  }, [
    impersonateClub?.clubId,
    location.search,
    selectedVirtualInvitation?.virtualMeetInvitationId,
    virtualInvitationModalVisible,
    visibleVirtualMeets,
  ])

  const openVirtualMeetShellEditor = useCallback((virtualMeetId: number) => {
    setSelectedVirtualMeetShellId(virtualMeetId)
    setVirtualMeetShellEditorVisible(true)
  }, [])

  const openAcceptedVirtualMeet = useCallback(
    (meetId: number) => {
      setMeetIdToEdit(meetId)
      setEditMeetModalVisible(true)
    },
    [],
  )

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const shouldOpenMeetActions = searchParams.get("openMeetActions") === "1"
    const meetId = parseInt(searchParams.get("meetId") || "", 10)
    const rawMeetActionTab = searchParams.get("meetActionTab")
    const meetActionTab =
      rawMeetActionTab === "reportStudio" ||
      rawMeetActionTab === "declarations" ||
      rawMeetActionTab === "eventOrder" ||
      rawMeetActionTab === "activityLog"
        ? rawMeetActionTab
        : null

    if (!shouldOpenMeetActions || !Number.isInteger(meetId) || meetId <= 0) {
      return
    }

    setMeetIdToEdit(meetId)
    setMeetActionsInitialTab(meetActionTab)
    setEditMeetModalVisible(true)

    searchParams.delete("meetId")
    searchParams.delete("openMeetActions")
    searchParams.delete("meetActionTab")

    history.replace({
      pathname: location.pathname,
      search: searchParams.toString() ? `?${searchParams.toString()}` : "",
    })
  }, [history, location.pathname, location.search])

  const openRemovedMeet = useCallback((meetId: number) => {
    setRemovedMeetIdToView(meetId)
    setRemovedMeetModalVisible(true)
  }, [])

  const openVirtualMeetResults = useCallback(
    (virtualMeetId: number) => {
      history.push(`/app/virtualMeet/results/${virtualMeetId}`)
    },
    [history],
  )


  if (loadingClubSeasons) return <PlaceholderSeasonDisplay />

  const pendingVirtualInvitations = visibleVirtualMeets.filter(
    (virtualMeet) => virtualMeet.status === "pending",
  )

  const acceptedVirtualMeets = visibleVirtualMeets.filter(
    (virtualMeet) =>
      virtualMeet.status === "accepted" &&
      Number.isInteger(virtualMeet.acceptedMeetId),
  )

  return (
    <>
      {slideIn && (
        <style>{`
          @keyframes msSlideInFromLeft {
            from { opacity: 0; transform: translateX(-36px); }
            to   { opacity: 1; transform: translateX(0); }
          }
          .ms-slide-back-in {
            animation: msSlideInFromLeft 0.28s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
          }
        `}</style>
      )}
      <div className={`all-club-seasons${slideIn ? " ms-slide-back-in" : ""}`}>
      <VirtualMeetInvitationSection
        invitations={pendingVirtualInvitations}
        onReviewInvitation={openVirtualInvitationModal}
      />
      {clubSeasons.map((clubSeasonDisplay, index) => {
        const clubSeasonId = clubSeasonDisplay.clubSeason.clubSeasonId
        const seasonViewMode = seasonViewModeById[clubSeasonId] || "regular"
        const seasonSortDirection = seasonSortDirectionById[clubSeasonId] || "asc"
        const seasonMeta = getSeasonHeaderMeta({
          startDate: clubSeasonDisplay.clubSeason.startDate,
          endDate: clubSeasonDisplay.clubSeason.endDate,
        })
        const seasonEnded =
          dayjs(clubSeasonDisplay.clubSeason.endDate).diff(dayjs(), "days") < 0

        return (
        <StyledSeasonDisplay
          key={`club_season_block_${clubSeasonId}_${index}`}
          $isFirstSeason={index === 0 && pendingVirtualInvitations.length === 0}
        >
          <Card
            variant="borderless"
            className={`season-header-card season-status-${seasonMeta.statusKey}${clubSeasonDisplay.visible ? "" : " season-collapsed"}`}
          >
            <div className="season-header-shell">
              <div className="season-header-main">
                <Button
                  type="text"
                  className="season-header-toggle"
                  icon={clubSeasonDisplay.visible ? <DownOutlined /> : <RightOutlined />}
                  onClick={() => updateClubSeasonVisible(clubSeasonId)}
                />
                <div className="season-header-copy">
                  <div className="season-header-title-row">
                    <Typography.Title level={4} className="season-header-title">
                      {clubSeasonDisplay.clubSeason.seasonName}
                    </Typography.Title>
                    <Tag color={seasonMeta.color}>{seasonMeta.label}</Tag>
                  </div>
                </div>
              </div>
              <Space className="season-header-controls" size={8} wrap>
                {!seasonEnded ? (
                  <Button
                    type="primary"
                    className="season-header-add-button"
                    onClick={() => {
                      if (isFeatureLocked(FeatureKey.addSwimMeet)) {
                        openLockedModal(FeatureKey.addSwimMeet)
                        return
                      }
                      openCreateMeetModal(
                        clubSeasonId,
                        clubSeasonDisplay.clubSeason.seasonId,
                        clubSeasonDisplay.clubSeason.seasonName,
                        dayjs(clubSeasonDisplay.clubSeason.startDate),
                        dayjs(clubSeasonDisplay.clubSeason.endDate),
                      )
                    }}
                  >
                    Add Swim Meet
                  </Button>
                ) : null}
                <Tooltip
                  title={
                    seasonViewMode === "compact"
                      ? "Switch to regular view"
                      : "Switch to compact view"
                  }
                >
                  <Button
                    type="default"
                    size="small"
                    icon={seasonViewMode === "compact" ? <ExpandOutlined /> : <CompressOutlined />}
                    onClick={() => toggleSeasonViewMode(clubSeasonId)}
                  />
                </Tooltip>
                <Tooltip
                  title={
                    seasonSortDirection === "asc"
                      ? "Sort newest to oldest"
                      : "Sort oldest to newest"
                  }
                >
                  <Button
                    type="default"
                    size="small"
                    icon={seasonSortDirection === "asc" ? <SortAscendingOutlined /> : <SortDescendingOutlined />}
                    onClick={() => toggleSeasonSortDirection(clubSeasonId)}
                  />
                </Tooltip>
              </Space>
            </div>
          </Card>
          {clubSeasonDisplay.visible ? (
            <ClubSeasonMeets
              triggerRefresh={triggerRefresh}
              seasonEnded={seasonEnded}
              setMeetIdToEdit={setMeetIdToEdit}
              setEditMeetModalVisible={setEditMeetModalVisible}
              openCreateMeetModal={() =>
                openCreateMeetModal(
                  clubSeasonId,
                  clubSeasonDisplay.clubSeason.seasonId,
                  clubSeasonDisplay.clubSeason.seasonName,
                  dayjs(clubSeasonDisplay.clubSeason.startDate),
                  dayjs(clubSeasonDisplay.clubSeason.endDate),
                )
              }
              clubSeasonId={clubSeasonId}
              viewMode={seasonViewMode}
              sortDirection={seasonSortDirection}
              acceptedVirtualMeets={acceptedVirtualMeets}
              participantMap={virtualMeetParticipants}
              loadingVirtualMeetParticipants={virtualMeetParticipantsLoading}
              activeClubId={impersonateClub?.clubId || null}
              onReviewInvitation={openVirtualInvitationModal}
              onManageShell={openVirtualMeetShellEditor}
              onOpenMeet={openAcceptedVirtualMeet}
              onOpenVirtualResults={openVirtualMeetResults}
              onOpenRemovedMeet={openRemovedMeet}
              onOpenStandings={(msmId, name, clubs) => {
                setStandingsModalMsmId(msmId)
                setStandingsModalName(name)
                setStandingsModalClubs(clubs)
              }}
            />
          ) : null}
        </StyledSeasonDisplay>
      )})}
      <CreateMeetModal
        visible={createMeetModalVisible}
        setVisible={setCreateMeetModalVisible}
        refreshSchedule={refreshSchedule}
        season={selectedSeason}
      />
      <ChampionshipStandingsModal
        visible={standingsModalMsmId != null}
        onClose={() => setStandingsModalMsmId(null)}
        multiSessionMeetId={standingsModalMsmId ?? 0}
        championshipName={standingsModalName}
        participatingClubs={standingsModalClubs}
      />
      <MeetActionsModal
        key={meetIdToEdit}
        meetId={meetIdToEdit}
        visible={editMeetModalVisible}
        setVisible={(newVisible) => {
          setEditMeetModalVisible(newVisible)
          if (!newVisible) {
            setMeetActionsInitialTab(null)
          }
        }}
        initialActiveTab={meetActionsInitialTab}
        refreshData={refreshSchedule}
      />
      <RemovedMeetModal
        visible={removedMeetModalVisible}
        setVisible={(newVisible) => {
          setRemovedMeetModalVisible(newVisible)
          if (!newVisible) {
            setRemovedMeetIdToView(null)
          }
        }}
        meetId={removedMeetIdToView}
      />
      <VirtualMeetInvitationResponseModal
        visible={virtualInvitationModalVisible}
        setVisible={(newVisible) => {
          setVirtualInvitationModalVisible(newVisible)
          if (!newVisible) {
            setSelectedVirtualInvitation(null)
            clearVirtualInvitationQueryParams()
          }
        }}
        virtualMeetInvitationId={selectedVirtualInvitation?.virtualMeetInvitationId || null}
        clubId={impersonateClub?.clubId || null}
        virtualMeetId={selectedVirtualInvitation?.virtualMeetId || null}
        initialOrganizerClubId={selectedVirtualInvitation?.organizerClubId || null}
        initialInvitedTeams={
          selectedVirtualInvitation?.virtualMeetId
            ? virtualMeetParticipants[selectedVirtualInvitation.virtualMeetId] || []
            : []
        }
        title="Review Virtual Meet Invitation"
        description="Choose the roster and pool your club will use for this invited virtual meet."
        onSuccess={() => {
          setSelectedVirtualInvitation(null)
          clearVirtualInvitationQueryParams()
          refreshSchedule()
        }}
      />
      <VirtualMeetShellEditorModal
        visible={virtualMeetShellEditorVisible}
        setVisible={(newVisible) => {
          setVirtualMeetShellEditorVisible(newVisible)
          if (!newVisible) {
            setSelectedVirtualMeetShellId(null)
          }
        }}
        virtualMeetId={selectedVirtualMeetShellId}
        refresh={refreshSchedule}
      />
    </div>
    </>
  )
}

interface MeetSchedulePageProps {
  renderWithinClubAccountShell?: boolean
}

export default function MeetSchedulePage({
  renderWithinClubAccountShell = false,
}: MeetSchedulePageProps = {}): JSX.Element {
  const [resourceMenuItems, setResourceMenuItems] = useState<any[]>([
    {
      key: "empty",
      disabled: true,
      label: <span style={{ color: "#8c8c8c" }}>No resources available</span>,
    },
  ])

  useEffect(() => {
    let isCancelled = false

    const loadResources = async () => {
      try {
        const response: any = await swimminglyApi.get("/api/swim-meet-resources")
        if (response?.status === "error") {
          throw new Error(response.message || "Unable to load swim meet resources")
        }

        const resources: SwimMeetResource[] = Array.isArray(response?.resources)
          ? response.resources
          : []

        if (isCancelled) return

        if (resources.length === 0) {
          setResourceMenuItems([
            {
              key: "empty",
              disabled: true,
              label: <span style={{ color: "#8c8c8c" }}>No resources available</span>,
            },
          ])
          return
        }

        setResourceMenuItems(
          resources.map((resource) => {
            const destinationUrl = resource.type === "upload" ? resource.cdnUrl : resource.url
            return {
              key: String(resource.id),
              icon: <FilePdfOutlined />,
              label: (
                <Tooltip title={resource.description || undefined} placement="left">
                  <div>
                    <a
                      href={destinationUrl || undefined}
                      target={resource.openInNewTab ? "_blank" : undefined}
                      rel={resource.openInNewTab ? "noopener noreferrer" : undefined}
                    >
                      {resource.title}
                    </a>
                  </div>
                </Tooltip>
              ),
            }
          }),
        )
      } catch (err) {
        console.error("MeetSchedulePage resources load error", err)
        if (!isCancelled) {
          setResourceMenuItems([
            {
              key: "load-error",
              disabled: true,
              label: <span style={{ color: "#8c8c8c" }}>Unable to load resources</span>,
            },
          ])
        }
      }
    }

    void loadResources()

    return () => {
      isCancelled = true
    }
  }, [])

  return (
    <div>
      {!renderWithinClubAccountShell ? (
        <>
          <header className="page-header">
            <h2><FontAwesomeIcon icon={faCalendar} />{" "}Meet Schedule</h2>
          </header>
          
          <PersistentClubBlockSlot />
        </>
      ) : null}
      
      <ContentPanel
        title="Meet Schedule"
        headerActions={
          <Dropdown menu={{ items: resourceMenuItems }}>
            <button
              type="button"
              style={{
                background: "none",
                border: "none",
                color: "#1890ff",
                cursor: "pointer",
                padding: 0,
                font: "inherit",
                textDecoration: "underline",
                whiteSpace: "nowrap",
              }}
            >
              Swim Meet Resources <DownOutlined />
            </button>
          </Dropdown>
        }
      >
        <div style={{ paddingBottom: 40 }}>
          <MeetSchedulePageInner />
        </div>
      </ContentPanel>
    </div>
  )
}
